import { useState, useEffect, useCallback } from 'react';
import {
    FileText, CheckCircle2, Sparkles, UserCheck, Loader2, AlertTriangle, Copy,
} from 'lucide-react';
import { getCompanyResumeAnalytics } from '@/services/apiClient';
import { toast } from 'sonner';

/**
 * The resume pipeline for one company.
 *
 * Laid out as three separate questions rather than one pile of counts: how much
 * came in, how much survived processing, and what the evaluation said. A
 * company can look busy on intake and still be getting almost nothing usable
 * out the far end, and nothing surfaced that before.
 */

const WINDOWS = [
    { key: '30', label: '30 days' },
    { key: '90', label: '90 days' },
    { key: 'all', label: 'All time' },
];

const TIER_TONE = {
    'Strong Match':  'bg-emerald-500',
    'Good Match':    'bg-teal-500',
    'Partial Match': 'bg-amber-500',
    'Weak Match':    'bg-destructive',
    'not tiered':    'bg-muted-foreground/40',
};

const CAUSE_LABEL = {
    template: 'Job template problem',
    format: 'Unreadable file format',
    ai: 'AI extraction failed',
    size: 'File too large',
    network: 'Network error',
    unknown: 'Unknown',
    uncategorised: 'No cause recorded',
};

const rate = (v) => (v == null ? '—' : `${v}%`);

/** A company figure against the platform average, when there is one. */
function Kpi({ icon: Icon, label, value, sub, benchmark, tone = 'default' }) {
    const delta = benchmark != null && typeof value === 'number'
        ? Math.round((value - benchmark) * 10) / 10
        : null;

    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
                <Icon className={`h-3.5 w-3.5 ${tone === 'warn' ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground'}`} />
                <p className="text-xs font-medium text-muted-foreground">{label}</p>
            </div>
            <p className="mt-1.5 text-2xl font-bold text-foreground">{value ?? '—'}</p>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
            {delta != null && (
                <p className={`mt-1 text-[11px] ${
                    delta >= 0 ? 'text-emerald-600 dark:text-emerald-500' : 'text-amber-600 dark:text-amber-500'
                }`}>
                    {delta >= 0 ? '+' : ''}{delta} vs platform average
                </p>
            )}
        </div>
    );
}

function Bar({ label, value, max = 100, tone = 'bg-primary' }) {
    const width = max > 0 ? Math.min(100, ((value ?? 0) / max) * 100) : 0;
    return (
        <div>
            <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium tabular-nums text-foreground">{value ?? '—'}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                <div className={`h-full rounded-full ${tone}`} style={{ width: `${width}%` }} />
            </div>
        </div>
    );
}

export default function ResumeAnalyticsPanel({ companyId }) {
    const [days, setDays] = useState('all');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getCompanyResumeAnalytics(companyId, days);
            setData(res.data);
        } catch (err) {
            toast.error(err.message || 'Could not load resume analytics.');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, days]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }
    if (!data) return null;

    const { intake, processing, evaluation, outcomes, byJob, monthly, benchmark } = data;
    const failures = Object.entries(processing.failuresByCause || {});
    const tiers = Object.entries(evaluation.byTier || {});
    const tierTotal = tiers.reduce((s, [, n]) => s + n, 0);
    const sources = intake.bySource || {};
    const profile = evaluation.scoreProfile || {};
    const maxMonth = Math.max(1, ...monthly.map(m => m.received));

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="font-semibold text-foreground">Resume pipeline</h3>
                    <p className="text-sm text-muted-foreground">
                        What came in, what made it through, and what the scoring said.
                    </p>
                </div>
                <div className="flex rounded-lg border border-border p-0.5">
                    {WINDOWS.map(w => (
                        <button
                            key={w.key} type="button" onClick={() => setDays(w.key)}
                            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                                days === w.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            {w.label}
                        </button>
                    ))}
                </div>
            </div>

            {intake.received === 0 ? (
                <p className="rounded-xl border border-border bg-card py-10 text-center text-sm text-muted-foreground">
                    No resumes in this period.
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <Kpi icon={FileText} label="Received" value={intake.received}
                            sub={`${sources.admin_upload || 0} uploaded · ${sources.self_applied || 0} applied${sources.unknown ? ` · ${sources.unknown} unknown` : ''}`} />
                        <Kpi icon={CheckCircle2} label="Parsed" value={processing.parsed}
                            sub={`${rate(processing.parseRate)} of attempts${processing.failed ? ` · ${processing.failed} failed` : ''}`}
                            benchmark={benchmark.parseRate}
                            tone={processing.failed > 0 ? 'warn' : 'default'} />
                        <Kpi icon={Sparkles} label="Evaluated" value={evaluation.evaluated}
                            sub={`avg score ${evaluation.avgScore ?? '—'} (${evaluation.minScore ?? '—'}–${evaluation.maxScore ?? '—'})`}
                            benchmark={benchmark.avgScore} />
                        <Kpi icon={UserCheck} label="Selected" value={outcomes.selected}
                            sub={`${rate(outcomes.selectionRate)} of parsed · ${outcomes.pending} pending`}
                            benchmark={benchmark.selectionRate} />
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                        Platform averages drawn from {benchmark.basedOnCompanies} companies
                        and {benchmark.basedOnResumes} resumes — a small sample, not an industry figure.
                    </p>

                    {/* Anything that stopped part-way is worth seeing on its own */}
                    {(processing.failed > 0 || processing.inFlight > 0 || intake.duplicates > 0
                        || (evaluation.evaluationRate != null && evaluation.evaluationRate < 100)) && (
                        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
                            <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-500" />
                                Not fully processed
                            </h4>
                            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                                {failures.map(([cause, n]) => (
                                    <li key={cause}>
                                        <strong className="text-foreground">{n}</strong> failed — {CAUSE_LABEL[cause] || cause}
                                    </li>
                                ))}
                                {processing.inFlight > 0 && (
                                    <li><strong className="text-foreground">{processing.inFlight}</strong> still in the queue</li>
                                )}
                                {evaluation.evaluationRate != null && evaluation.evaluationRate < 100 && (
                                    <li>
                                        <strong className="text-foreground">{processing.parsed - evaluation.evaluated}</strong>{' '}
                                        parsed but never scored
                                    </li>
                                )}
                                {intake.duplicates > 0 && (
                                    <li className="flex items-center gap-1.5">
                                        <Copy className="h-3 w-3" />
                                        <span><strong className="text-foreground">{intake.duplicates}</strong> duplicate uploads detected</span>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    <div className="grid gap-4 lg:grid-cols-2">
                        {/* Tier spread */}
                        <div className="rounded-xl border border-border bg-card p-4">
                            <h4 className="text-sm font-semibold text-foreground">Candidate quality</h4>
                            <p className="mb-3 text-xs text-muted-foreground">
                                How the scoring rated each parsed resume.
                            </p>
                            {tierTotal > 0 ? (
                                <>
                                    <div className="flex h-2.5 overflow-hidden rounded-full">
                                        {tiers.map(([tier, n]) => (
                                            <div key={tier} className={TIER_TONE[tier] || 'bg-muted'}
                                                style={{ width: `${(n / tierTotal) * 100}%` }} title={`${tier}: ${n}`} />
                                        ))}
                                    </div>
                                    <ul className="mt-3 space-y-1 text-xs">
                                        {tiers.map(([tier, n]) => (
                                            <li key={tier} className="flex items-center justify-between gap-2">
                                                <span className="flex items-center gap-2">
                                                    <span className={`h-2 w-2 rounded-full ${TIER_TONE[tier] || 'bg-muted'}`} />
                                                    <span className={tier === 'not tiered' ? 'text-muted-foreground' : 'text-foreground'}>
                                                        {tier === 'not tiered' ? 'Scored before tiers existed' : tier}
                                                    </span>
                                                </span>
                                                <span className="tabular-nums text-muted-foreground">
                                                    {n} · {Math.round((n / tierTotal) * 100)}%
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <p className="py-4 text-center text-sm text-muted-foreground">Nothing scored yet.</p>
                            )}
                        </div>

                        {/* Where candidates are strong and weak */}
                        <div className="rounded-xl border border-border bg-card p-4">
                            <h4 className="text-sm font-semibold text-foreground">Average score profile</h4>
                            <p className="mb-3 text-xs text-muted-foreground">
                                Which dimensions their applicant pool is strong and weak on.
                            </p>
                            <div className="space-y-2">
                                <Bar label="Technical skills"     value={profile.technicalSkills} />
                                <Bar label="Experience relevance" value={profile.experienceRelevance} />
                                <Bar label="Education fit"        value={profile.educationFit} />
                                <Bar label="Keyword coverage"     value={profile.keywordCoverage} />
                                <Bar label="Achievement quality"  value={profile.achievementQuality} />
                                <Bar label="Seniority alignment"  value={profile.seniorityAlignment} />
                            </div>
                            {evaluation.avgSkillMatch != null && (
                                <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
                                    Average declared-skill match:{' '}
                                    <strong className="text-foreground">{evaluation.avgSkillMatch}%</strong>
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Per job */}
                    {byJob.length > 0 && (
                        <div className="rounded-xl border border-border bg-card p-4">
                            <h4 className="mb-3 text-sm font-semibold text-foreground">By job description</h4>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-border text-left">
                                            <th className="pb-2 font-medium text-muted-foreground">Job</th>
                                            <th className="pb-2 text-right font-medium text-muted-foreground">Received</th>
                                            <th className="pb-2 text-right font-medium text-muted-foreground">Parsed</th>
                                            <th className="pb-2 text-right font-medium text-muted-foreground">Failed</th>
                                            <th className="pb-2 text-right font-medium text-muted-foreground">Avg score</th>
                                            <th className="pb-2 text-right font-medium text-muted-foreground">Selected</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {byJob.map(j => (
                                            <tr key={j.templateId || 'none'}>
                                                <td className="py-2 pr-3">{j.title}</td>
                                                <td className="py-2 text-right tabular-nums">{j.received}</td>
                                                <td className="py-2 text-right tabular-nums">{j.parsed}</td>
                                                <td className={`py-2 text-right tabular-nums ${j.failed > 0 ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>
                                                    {j.failed || '—'}
                                                </td>
                                                <td className="py-2 text-right tabular-nums">{j.avgScore ?? '—'}</td>
                                                <td className="py-2 text-right tabular-nums">{j.selected}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Trend */}
                    {monthly.length > 1 && (
                        <div className="rounded-xl border border-border bg-card p-4">
                            <h4 className="mb-3 text-sm font-semibold text-foreground">Volume by month</h4>
                            <div className="flex items-end gap-2" style={{ height: 90 }}>
                                {monthly.map(m => (
                                    <div key={m.period} className="flex flex-1 flex-col items-center gap-1">
                                        <div className="flex w-full flex-1 items-end justify-center">
                                            <div
                                                className="w-full rounded-t bg-primary/80"
                                                style={{ height: `${(m.received / maxMonth) * 100}%`, minHeight: 3 }}
                                                title={`${m.period}: ${m.received} received, ${m.parsed} parsed, ${m.selected} selected`}
                                            />
                                        </div>
                                        <span className="text-[10px] tabular-nums text-muted-foreground">
                                            {m.period.slice(5)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-2 text-[11px] text-muted-foreground">
                                Hover a bar for parsed and selected counts.
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
